import React, { useState } from "react";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaHourglassHalf,
  FaBan,
  FaFilter,
} from "react-icons/fa";
import DashboardFilterDialogWrapper from "./FilterDialog/DashboardFilterWrapper";

type DashboardProps = {
  data: {
    videoLoading: boolean;
    videoLectureOrders?: {
      success?: number;
      pending?: number;
      failed?: number;
      cancelled?: number;
    };
    razorpayLoading: boolean;
    razorpay?: {
      success?: number;
      failed?: number;
      pending?: number;
      totalOrder?: number;
    };
  };
};

const Dashboard = ({ data }: DashboardProps) => {
  const [openFilter, setOpenFilter] = useState(false);

  // Safe fallback for API data
  const videoLectureOrders = data?.videoLectureOrders || {
    success: 0,
    pending: 0,
    failed: 0,
    cancelled: 0,
  };
  const videoLoading = data?.videoLoading;
  const razorpayLoading = data?.razorpayLoading;
  const razorpay = data?.razorpay || { success: 0, failed: 0 };

  return (
    <div className="p-6 space-y-6 relative">
      {/* Header with Filter Button */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
        <button
          onClick={() => setOpenFilter(true)}
          className="flex items-center gap-2 bg-gray-800 text-white px-4 py-2 rounded-lg shadow  transition"
        >
          <FaFilter />
          <span>Filter</span>
        </button>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Video Lecture Orders */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">
            Orders - Video Lectures
          </h2>

          {videoLoading ? (
            <div className="flex justify-center items-center h-24">
              <span className="text-gray-500">Loading...</span>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              <li className="flex justify-between items-center py-2">
                <div className="flex items-center gap-2">
                  <FaCheckCircle className="text-green-600" />
                  <span>Success</span>
                </div>
                <span className="font-semibold">
                  {videoLectureOrders.success}
                </span>
              </li>
              <li className="flex justify-between items-center py-2">
                <div className="flex items-center gap-2">
                  <FaHourglassHalf className="text-yellow-600" />
                  <span>Pending</span>
                </div>
                <span className="font-semibold">
                  {videoLectureOrders.pending}
                </span>
              </li>
              <li className="flex justify-between items-center py-2">
                <div className="flex items-center gap-2">
                  <FaTimesCircle className="text-red-600" />
                  <span>Failed</span>
                </div>
                <span className="font-semibold">
                  {videoLectureOrders.failed}
                </span>
              </li>
              <li className="flex justify-between items-center py-2">
                <div className="flex items-center gap-2">
                  <FaBan className="text-gray-600" />
                  <span>Cancelled</span>
                </div>
                <span className="font-semibold">
                  {videoLectureOrders.cancelled}
                </span>
              </li>
            </ul>
          )}
        </div>

        {/* RazorPay Orders */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">RazorPay</h2>

          {razorpayLoading ? (
            <div className="flex justify-center items-center h-24">
              <span className="text-gray-500">Loading...</span>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              <li className="flex justify-between items-center py-2">
                <div className="flex items-center gap-2">
                  <FaCheckCircle className="text-green-600" />
                  <span>Success</span>
                </div>
                <span className="font-semibold">{razorpay.success}</span>
              </li>
              <li className="flex justify-between items-center py-2">
                <div className="flex items-center gap-2">
                  <FaTimesCircle className="text-red-600" />
                  <span>Failed</span>
                </div>
                <span className="font-semibold">{razorpay.failed}</span>
              </li>
              <li className="flex justify-between items-center py-2">
                <div className="flex items-center gap-2">
                  <FaHourglassHalf className="text-yellow-600" />
                  <span>Pending</span>
                </div>
                <span className="font-semibold">{razorpay.pending}</span>
              </li>
              <li className="flex justify-between items-center py-2">
                <div className="flex items-center gap-2">
                  <FaCheckCircle className="text-blue-600" />
                  <span>Total Orders</span>
                </div>
                <span className="font-semibold">{razorpay.totalOrder}</span>
              </li>
            </ul>
          )}
        </div>
      </div>

      {/* Filter Dialog */}
      {openFilter && (
        <DashboardFilterDialogWrapper onClose={() => setOpenFilter(false)} />
      )}
    </div>
  );
};

export default Dashboard;
